import React, { useState } from 'react';
import { customersAPI } from '../services/api';
import DuplicateSubscriptionsModal from './DuplicateSubscriptionsModal';
import './ImportCustomersModal.css';

function ImportCustomersModal({ onClose, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [categoryEndDate, setCategoryEndDate] = useState('');

  // Category definitions
  const CATEGORIES = {
    1: { name: 'קטגוריה 1: 4 ספרים לחצי שנה', baseAmount: 50, maxBooks: 4 },
    2: { name: 'קטגוריה 2: ספר 1 לחצי שנה', baseAmount: 15, maxBooks: 1 },
    3: { name: 'קטגוריה 3: ספר 1 להשאלה', baseAmount: 30, maxBooks: 1 }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setError('רק קבצי Excel מותרים (.xlsx או .xls)');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('בחר קובץ להעלאה');
      return;
    }

    // Show popup for category end date
    setShowCategoryPopup(true);
  };

  const handleConfirmImport = async () => {
    if (!endDate) {
      setError('בחר תאריך סיום תקופת המנוי');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('categoryEndDate', endDate);

      const response = await customersAPI.importFromExcel(formData);

      // בדוק אם יש לקוחות כפולים עם מנוי פעיל
      if (response.data.duplicates && response.data.duplicates.length > 0) {
        setDuplicates(response.data.duplicates);
        setCategoryEndDate(endDate);
        setShowCategoryPopup(false);
        setShowDuplicateModal(true);
      } else {
        setSuccessMessage(`${response.data.imported} לקוחות יובאו בהצלחה`);
        setFile(null);
        setEndDate('');
        setShowCategoryPopup(false);

        setTimeout(() => {
          onImportComplete();
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בייבוא הלקוחות');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateConfirm = async (action) => {
    try {
      setLoading(true);
      const response = await customersAPI.processDuplicateSubscriptions({
        duplicates,
        action,
        categoryEndDate
      });

      setSuccessMessage(`${response.data.processed} לקוחות עודכנו בהצלחה`);
      setShowDuplicateModal(false);
      setFile(null);
      setEndDate('');
      setDuplicates([]);

      setTimeout(() => {
        onImportComplete();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בעדכון הלקוחות');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>ייבוא לקוחות מ-Excel</h2>

        {!showCategoryPopup ? (
          <>
            <div className="import-instructions">
              <h4>הוראות ייבוא:</h4>
              <ul>
                <li><strong>שדות חובה:</strong> מספר זהות, שם</li>
                <li><strong>שדות אופציונליים:</strong> כתובת, טלפון, מייל</li>
                <li><strong>שדות נוספים:</strong> סכום, תשלומים, קטגוריה</li>
              </ul>
              <h4>קטגוריות:</h4>
              <ol>
                <li>4 ספרים לחצי שנה - 50 ש"ח</li>
                <li>ספר 1 לחצי שנה - 15 ש"ח</li>
                <li>ספר 1 להשאלה - 30 ש"ח</li>
              </ol>
            </div>

            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              {file && <span className="file-name">✓ {file.name}</span>}
            </div>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!file || loading}
              >
                {loading ? 'ממתין...' : 'ייבא'}
              </button>
              <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                ביטול
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="category-popup-content">
              <p><strong>בחר את תאריך סיום תקופת המנויים לחצי שנה:</strong></p>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading}
                style={{ marginBottom: '20px', padding: '8px', fontSize: '16px' }}
              />
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmImport}
                  disabled={!endDate || loading}
                >
                  {loading ? 'ממתין...' : 'אישור'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCategoryPopup(false);
                    setError('');
                  }}
                  disabled={loading}
                >
                  חזור
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showDuplicateModal && (
        <DuplicateSubscriptionsModal
          duplicates={duplicates}
          categoryEndDate={categoryEndDate}
          loading={loading}
          onClose={() => {
            setShowDuplicateModal(false);
            setDuplicates([]);
            setFile(null);
            setEndDate('');
            onClose();
          }}
          onConfirm={handleDuplicateConfirm}
        />
      )}

      {successMessage && (
        <div className="success-overlay">
          <div className="success-message-box">
            <p>{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportCustomersModal;
